<script setup>
import RegionItem from '@/components/PartnerList/RegionItem.vue'
import { computed, ref, nextTick, watch, defineAsyncComponent, useTemplateRef } from 'vue'
import CityPin from '@/components/PartnerList/CityPin.vue'
import TypeItem from '@/components/PartnerList/TypeItem.vue'
import ActivityItem from '@/components/PartnerList/ActivityItem.vue'
import { createReusableTemplate } from '@vueuse/core'
const props = defineProps({
  regions: Array,
  types: Array,
  counts: Object,
  activitiesByRegionsTypeAndCities: Object,
})

const InteractiveMap = defineAsyncComponent(
  () => import(/* webpackChunkName: "interactive-map-chunk" */ '../../map/InteractiveMap'),
)

const [DefineTemplate, ReuseTemplate] = createReusableTemplate()

const navWrapper = useTemplateRef('navWrapper')

const filters = ref({
  first: 'region',
  region: null,
  type: null,
})

// nav scroll
watch(
  filters,
  (filters) => {
    let id = `nav-${filters.first}`

    if (filters.first === 'type') {
      if (filters.type !== null) {
        id += `-${filters.type}`
        if (filters.region !== null) {
          id += `-${filters.region}`
        }
      }
    } else if (filters.region !== null) {
      id += `-${filters.region}`
      if (filters.type !== null) {
        id += `-${filters.type}`
      }
    }

    return nextTick(() => {
      const yPos = navWrapper.value.querySelector(`#${id}`)?.offsetTop ?? 0
      const stickyHeaderHeight = 80

      return navWrapper.value.scroll(0, Math.max(yPos - stickyHeaderHeight, 0))
    })
  },
  { deep: true },
)

const selectedRegionSlug = computed({
  get() {
    if (filters.value.region === null || (filters.value.first === 'type' && filters.value.type === null)) {
      return null
    }

    return props.regions.find(({ id }) => id === filters.value.region).slug
  },
  set(slug) {
    if (slug === null) {
      filters.value.region = null
      return
    }

    const region = props.regions.find((region) => slug === region.slug)

    if (region === undefined) {
      return
    }

    if (filters.value.first === 'type' && filters.value.type === null) {
      filters.value.first = 'region'
    }

    filters.value.region = filters.value.region !== region.id ? region.id : null
  },
})

const toggleType = (type) => {
  filters.value.type = filters.value.type !== type ? type : null
}

const toggleRegion = (region) => {
  filters.value.region = filters.value.region !== region ? region : null
}

const toggleFilterOrder = (first) => {
  filters.value = {
    first,
    region: null,
    type: null,
  }
}
</script>

<template>
  <div class="mt-8 flex items-center">
    <h3 class="mr-4 hidden text-xl font-medium md:block">
      {{ $t('activities.list.select') }}
    </h3>
    <div
      class="flex w-full items-center justify-around overflow-hidden rounded-lg border border-light/25 text-xs text-light shadow focus:outline-none md:w-auto">
      <button
        v-for="option in ['region', 'type']"
        :class="filters.first === option ? 'bg-secondary text-white' : 'bg-white'"
        class="block w-full px-4 py-3 text-center font-sans capitalize transition-all duration-300 ease-in-out md:w-28"
        @click="toggleFilterOrder(option)">
        {{ option }}
      </button>
    </div>
  </div>

  <div
    class="mt-8 grid select-none grid-cols-1 lg:grid-cols-[minmax(320px,1fr)_minmax(640px,780px)] lg:grid-rows-[minmax(640px,780px)]">
    <nav
      ref="navWrapper"
      class="relative snap-y scroll-py-2 overflow-y-scroll scroll-smooth lg:max-h-[780px]">
      <DefineTemplate v-slot="{ region, type }">
        <ul class="mt-4 space-y-4">
          <li
            v-for="(activities, city) in activitiesByRegionsTypeAndCities[`${region.id}-${type.id}`]"
            :key="city"
            class="snap-start">
            <CityPin :name="city" />
            <ul class="mt-1 space-y-2">
              <li
                v-for="activity in activities"
                :key="activity.id"
                class="snap-start">
                <ActivityItem :activity="activity" />
              </li>
            </ul>
          </li>
        </ul>
      </DefineTemplate>

      <ul
        v-if="filters.first === 'type'"
        id="nav-type">
        <template v-for="type in types">
          <li
            v-if="counts.types[type.id] > 0"
            :id="`nav-type-${type.id}`"
            :key="type.id"
            class="snap-start">
            <TypeItem
              class="z-20 border-b border-light/25"
              :selected="filters.type === type.id"
              :type="type"
              :count="counts.types[type.id]"
              @click="toggleType(type.id)" />
            <ul
              v-show="filters.type === type.id"
              class="mt-4 space-y-4">
              <template v-for="region in regions">
                <li
                  v-if="counts.regionsAndTypes[`${region.id}-${type.id}`] > 0"
                  :id="`nav-type-${type.id}-${region.id}`"
                  :key="region.id"
                  class="relative snap-start first:snap-align-none">
                  <RegionItem
                    class="top-[56px] z-10 rounded-xl border"
                    :selected="filters.region === region.id"
                    :region="region"
                    :count="counts.regionsAndTypes[`${region.id}-${type.id}`]"
                    @click="toggleRegion(region.id)" />
                  <ReuseTemplate
                    v-show="filters.region === region.id"
                    :region="region"
                    :type="type" />
                </li>
              </template>
            </ul>
          </li>
        </template>
      </ul>

      <ul
        v-if="filters.first === 'region'"
        id="nav-region">
        <template v-for="region in regions">
          <li
            v-if="counts.regions[region.id] > 0"
            :id="`nav-region-${region.id}`"
            :key="region.id"
            class="snap-start">
            <RegionItem
              class="z-20 border-b border-light/25"
              :selected="filters.region === region.id"
              :region="region"
              :count="counts.regions[region.id]"
              @click="toggleRegion(region.id)" />
            <ul
              v-show="filters.region === region.id"
              class="mt-4 space-y-4">
              <template v-for="type in types">
                <li
                  v-if="counts.regionsAndTypes[`${region.id}-${type.id}`] > 0"
                  :id="`nav-region-${region.id}-${type.id}`"
                  :key="type.id"
                  class="relative snap-start">
                  <TypeItem
                    class="top-[56px] z-10 rounded-xl border"
                    :selected="filters.type === type.id"
                    :type="type"
                    :count="counts.regionsAndTypes[`${region.id}-${type.id}`]"
                    @click="toggleType(type.id)" />
                  <ReuseTemplate
                    v-show="filters.type === type.id"
                    :region="region"
                    :type="type" />
                </li>
              </template>
            </ul>
          </li>
        </template>
      </ul>
    </nav>
    <InteractiveMap
      id="svg-map"
      v-model="selectedRegionSlug"
      class="order-first aspect-square rounded-xl bg-lightgray lg:order-last" />
  </div>
</template>
